import { DropdownOption } from '@tcl-argon-models/models';
import Loader from '@tcl-boron-prefabs/loader';
import cn from 'classnames';
import {
  GetPropsCommonOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetMenuPropsOptions,
  UseSelectGetItemPropsOptions,
  UseSelectGetMenuPropsOptions,
} from 'downshift';
import * as React from 'react';
import { BoldedLabel } from './BoldedLabel/BoldedLabel';
import { itemIsSelected, itemToString } from './coreUtils';
import { ActionPalette } from '@tcl-boron-colors/colors';
import { SelectCheckbox } from './SelectCheckbox';

interface MenuProps {
  classes: Record<string, string>;
  isOpen: boolean;
  styles: {
    [key: string]: React.CSSProperties;
  };
  attributes: {
    [key: string]:
      | {
          [key: string]: string;
        }
      | undefined;
  };
  getItemProps: (
    options: UseSelectGetItemPropsOptions<DropdownOption> | UseComboboxGetItemPropsOptions<DropdownOption>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  items: Array<DropdownOption>;
  highlightedIndex: number;
  getMenuProps: (
    options?: UseSelectGetMenuPropsOptions | UseComboboxGetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  isSingleSelect: boolean;
  selectedItems: DropdownOption[];
  inputValue?: string;
  isLoading?: boolean;
  isTypeahead: boolean;
  clearable: boolean;
  defaultOptionsLabel?: string;
  rowVirtualizer?: any;
}

export const Menu: React.ForwardRefExoticComponent<MenuProps & React.RefAttributes<HTMLInputElement>> =
  React.forwardRef(
    (
      {
        classes,
        isOpen,
        styles,
        attributes,
        getItemProps,
        highlightedIndex,
        items,
        getMenuProps,
        isSingleSelect,
        selectedItems,
        inputValue = '',
        isLoading,
        isTypeahead,
        clearable,
        defaultOptionsLabel,
        rowVirtualizer,
      },
      popperRef,
    ) => {
      const getGrids = () => {
        const initialGrids = rowVirtualizer.virtualItems.map((virtualRow) => {
          const item = items[virtualRow.index];
          if (item) {
            const isSelected = itemIsSelected(item, selectedItems);
            const itemProps = getItemProps({
              index: virtualRow.index,
              item: item,
            });
            const singleSelectClearOption = (label: string) => (
              <div
                className={cn(classes.clearSelectionWrapper, {
                  [classes.multiClearWrapper]: !selectedItems || selectedItems.length === 0,
                })}
              >
                {selectedItems && selectedItems.length > 0 && !inputValue ? (
                  <div
                    className={cn(
                      classes.flexbox,
                      classes.flexboxWrap,
                      classes.selectCheckboxLabel,
                      classes.selectNoneCheckboxLabel,
                    )}
                    {...{ ...itemProps }}
                  >
                    {label}
                  </div>
                ) : (
                  <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
                    <strong>{label}</strong>
                  </p>
                )}
              </div>
            );
            const multiSelectClearableButton = (label: string) => (
              <div className={cn(classes.clearSelectionWrapper, classes.multiClearWrapper)}>
                <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
                  <strong>{label}</strong>
                </p>
                {selectedItems && selectedItems.length > 0 && !inputValue && (
                  <button
                    {...{ ...itemProps, ref: undefined }}
                    className={`${classes.clearSelectionsButton} ${
                      highlightedIndex === virtualRow.index ? classes.clearSelectionsButtonHover : ''
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>
            );
            const multiSelectSelectedLabel = (label: string) => (
              <div className={cn(classes.clearSelectionWrapper, classes.multiClearWrapper)}>
                <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
                  <strong>{label}</strong>
                </p>
              </div>
            );
            const singleSelectSelectedLabel = (label: string) => (
              <div className={cn(classes.clearSelectionWrapper, classes.multiClearWrapper)}>
                <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
                  <strong>{label}</strong>
                </p>
              </div>
            );
            const divider = <div tabIndex={-1} className={classes.selectedItemsDivider} />;
            const suggestionsText = (
              <p className={cn(classes.selectedText, classes.selectedTextHierarchical, classes.dividerLabel)}>
                <strong>{defaultOptionsLabel}</strong>
              </p>
            );
            return item.value === 'clear' ? (
              <li
                key={item.value}
                className={classes.virtualScrollRows}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  minHeight:
                    (isSingleSelect && !isTypeahead) ||
                    (isSingleSelect && isTypeahead && selectedItems && selectedItems.length > 0)
                      ? '34px'
                      : '22px',
                  backgroundColor:
                    isSingleSelect &&
                    selectedItems &&
                    selectedItems.length > 0 &&
                    !inputValue &&
                    highlightedIndex === virtualRow.index
                      ? `${ActionPalette[200]}`
                      : 'inherit',
                }}
                ref={virtualRow.measureRef}
              >
                {isSingleSelect && clearable && singleSelectClearOption(item.label)}
                {!isSingleSelect && clearable && multiSelectClearableButton(item.label)}
                {isSingleSelect && !clearable && singleSelectSelectedLabel(item.label)}
                {!isSingleSelect && !clearable && multiSelectSelectedLabel(item.label)}
              </li>
            ) : item.value === 'divider_and_label' ? (
              <li
                key={item.value}
                className={classes.virtualScrollRows}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  minHeight: isTypeahead ? '38px' : '15px',
                }}
                ref={virtualRow.measureRef}
              >
                {divider}
                {isTypeahead && suggestionsText}
              </li>
            ) : (
              <li
                key={item.value}
                className={classes.virtualScrollRows}
                style={{
                  backgroundColor: highlightedIndex === virtualRow.index ? `${ActionPalette[200]}` : 'inherit',
                  transform: `translateY(${virtualRow.start}px)`,
                  minHeight: '34px',
                  cursor: 'pointer',
                }}
                ref={virtualRow.measureRef}
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                {isSingleSelect && (
                  <div
                    className={cn(classes.flexbox, classes.selectCheckboxLabel, {
                      [classes.singleSelectedItemLabel]: isSelected,
                    })}
                    {...{ ...itemProps }}
                  >
                    <BoldedLabel
                      highlightJustBeginning={false}
                      inputValue={inputValue}
                      optionLabel={`${itemToString(item)}`}
                      className={classes.boldedLabel}
                    />
                  </div>
                )}
                {!isSingleSelect && (
                  <div className={`${classes.flexbox}`}>
                    <SelectCheckbox
                      isSelected={isSelected}
                      label={item.label}
                      inputValue={inputValue}
                      item={item}
                      classes={classes}
                      highlightedFromParent={highlightedIndex === virtualRow.index}
                      shouldShowTreeStructure={false}
                      isHierarchical={false}
                      {...{ ...itemProps }}
                    />
                  </div>
                )}
              </li>
            );
          }
        });
        return initialGrids;
      };

      const grids = getGrids();

      return (
        <ul
          {...getMenuProps({
            ref: popperRef,
            className: `${classes.menuStyles} ${classes.scrollbar} ${
              isOpen && !isLoading && items.length === 0 ? classes.noResultsFoundMenu : ''
            } ${isOpen && isLoading ? classes.loaderMenu : ''} ${
              !isOpen || (!inputValue && selectedItems.length === 0 && items.length === 0) ? classes.invisibleMenu : ''
            }`,
            style: styles.popper,
            ...(attributes?.popper !== undefined ? attributes.popper : {}),
          })}
        >
          {isOpen && isLoading && (
            <div className={classes.menuLoaderAndNoOp}>
              <Loader size="large" loaderType="secondary" />
            </div>
          )}
          {isOpen && !isLoading && items.length === 0 && (
            <div className={classes.menuLoaderAndNoOp}>No Results Found.</div>
          )}
          {isOpen && !isLoading && items.length > 0 && (
            <>
              <div className={cn(classes.relativeMenuContainer)}>
                <li key="total-size" style={{ height: rowVirtualizer.totalSize }} />
                {grids}
              </div>
            </>
          )}
        </ul>
      );
    },
  );
Menu.displayName = 'Menu';
